import useDocumentTitle from 'hooks/useDocumentTitle'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import { toast } from 'react-toastify'
import { Alert, Container } from 'reactstrap'
import platformInstagram from 'assets/icons/instagram-logo.svg'
import platformYoutube from 'assets/icons/youtube-logo.svg'
import CampaignImageSlider from 'Components/campaign/CampaignImageSlider'
import { fetchCampaignThunk } from 'slices/campaigns/thunk'
import { AppDispatch, RootState, store } from 'store'
import { Milestone } from 'types/collaboration'
import { arrayOfUniqueStrings, getErrorMessage, toValidNumber } from 'utils/helpers'
import inModal from 'utils/hoc/inModal'
import './style.scss'
import SelectCreatorModal from 'Components/campaign/SelectCreatorModal'
import { Contact } from 'types/creators'
import { SocialAccount } from 'types/influencer'
import CampaignApplicationConfirmationModal, { ApplicationSubmitError } from 'Components/campaign/CampaignApplicationConfirmationModal'
import CampaignApplicationNegotiationModal from 'Components/campaign/CampaignApplicationNegotiationModal'
import CampaignSocialPostSelectionModal from 'Components/campaign/CampaignSocialPostSelectionModal'
import { applyCampaign } from 'services/api/campaign'
import { fetchApplicationsThunk } from 'slices/applications/thunk'
import InsufficientPermissionsError from 'Components/campaign/errors/InsufficientPermissionsError'

export const platforms = {
    ig: { id: 'ig', name: 'Instagram' },
    yt: { id: 'yt', name: 'YouTube' },
}

type SpecItem = {
    label: string
    value?: string
    values?: string[]
    isList?: boolean
    isMultiLine?: boolean
    textColor?: string
}

export const CampaignSpecsRow: React.FC<{ col1: React.ReactNode; col2: React.ReactNode }> = ({
    col1,
    col2,
}) => (
    <div className="campaign-detail-specs-row">
        <div className="campaign-detail-specs-row__col">{col1}</div>
        <div className="campaign-detail-specs-row__divider" aria-hidden />
        <div className="campaign-detail-specs-row__col">{col2}</div>
    </div>
)

const ShowMoreContent: React.FC<{ showMore: { label: string; values: string[] } | null }> = ({
    showMore,
}) => (
    <div className="campaign-detail-show-more pb-2">
        <div className="campaign-detail-show-more__header py-3">
            <span className="campaign-detail-show-more__title">{showMore?.label}</span>
        </div>
        <div className="campaign-detail-show-more__list">
            {showMore?.values.map((value, index) => (
                <div key={index} className="campaign-detail-show-more__item">
                    <span>{value}</span>
                </div>
            ))}
        </div>
    </div>
)

export const ShowMoreContentModal = inModal(ShowMoreContent, '')

const CampaignDetail = () => {
    const { campaignId } = useParams<{ campaignId: string }>()
    const navigate = useNavigate()
    const dispatch: AppDispatch = useDispatch()
    const { campaignLoading } = useSelector((state: RootState) => state.campaigns)
    const campaignData = useSelector(
        (state: RootState) => state.campaigns.campaigns[campaignId!]?.data
    )
    const languages = useSelector((state: RootState) => state.auth.master?.language_list) ?? {}
    const [showMore, setShowMore] = useState<{ label: string; values: string[] } | null>(null)
    const [showCreatorModal, setShowCreatorModal] = useState(false)
    const [selectedApplicant, setSelectedApplicant] = useState<{ creator: Contact, socialAccount: SocialAccount } | null>(null)
    const [selectedSocialPostId, setSelectedSocialPostId] = useState<string | null>(null)
    const [showSocialPostModal, setShowSocialPostModal] = useState(false)
    const [showNegotiationModal, setShowNegotiationModal] = useState(false)
    const [pendingApplication, setPendingApplication] = useState<{ languageId?: string, answers?: Record<string, string | number> } | null>(null)
    const hubId = useSelector((state: RootState) => state.auth.selectedHubId)
    useDocumentTitle(campaignData?.campaign?.name ?? 'Campaign')
    const [applicationError, setApplicationError] = useState<ApplicationSubmitError | null>(null)
    const canApply = campaignData?.can_apply ?? false
    const canApplyReason = campaignData?.can_apply_reason ?? null

    useEffect(() => {
        if (campaignId) {
            dispatch(fetchCampaignThunk({ campaignId }))
        }
    }, [campaignId, dispatch])

    const campaign = campaignData?.campaign
    const brand = campaignData?.brand ?? campaign?.brand
    const milestoneCount = campaignData?.milestones?.length ?? 0

    const sortedMilestones = useMemo(() => {
        return [...(campaignData?.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order)
    }, [campaignData?.milestones])

    const canApplyToCampaign = useMemo(() => {
        const milestone = sortedMilestones[0];
        if (!milestone) return false
        return (campaign?.require_hub && milestone.hub_commission_rate !== null)
    }, [campaign?.require_hub, sortedMilestones])

    const hubCommissionDisplay = useMemo(() => {
        if (!canApplyToCampaign || !canApply ) return null

        let maxFlat = 0
        let maxPercent = 0

        for (const milestone of sortedMilestones) {
            const rate = milestone.hub_commission_rate ?? milestone.hub_commision_rate
            const value = toValidNumber(rate?.value)
            if (value <= 0 || !rate?.type) continue
            if (rate.type === 'flat') maxFlat = Math.max(maxFlat, value)
            if (rate.type === 'percent') maxPercent = Math.max(maxPercent, value)
        }

        const amounts: string[] = []
        if (maxFlat > 0) amounts.push(`₹${maxFlat}`)
        if (maxPercent > 0) amounts.push(`${maxPercent}%`)
        return amounts.length > 0 ? amounts : null
    }, [campaign?.require_hub, sortedMilestones])

    const getCampaignImages = () => {
        let images: string[] = campaign?.hero_img_url ? [campaign.hero_img_url] : []
        if (campaign?.gallery_images?.length) {
            images = [...images, ...campaign.gallery_images]
        }
        return arrayOfUniqueStrings(images)
    }

    const campaignShareURL = useMemo(() => {
        if (!campaignData?.share_url) return ''
        let shareUrl = campaignData.share_url
        const referral = store.getState().auth.user?.referral_code
        if (referral) shareUrl += '/?referrer=' + referral
        return shareUrl
    }, [campaignData])

    const shareURL = async () => {
        if (!campaignShareURL) return
        try {
            await navigator.clipboard.writeText(campaignShareURL)
            toast.success('URL copied to your clipboard! 🙂')
        } catch {
            toast.error('Failed to copy link')
        }
    }

    const handleShare = async () => {
        if (!campaignData || !campaignShareURL) return
        if (navigator.share) {
            try {
                await navigator.share({
                    title: campaignData.campaign?.name,
                    url: campaignShareURL,
                })
                return
            } catch {
                // fall through to clipboard
            }
        }
        await shareURL()
    }

    const handleApply = async (
        languageId?: string,
        answers?: Record<string, string | number>,
        socialPostId?: string,
        quotedOffer?: string | number
    ) => {
        if (!selectedApplicant || !campaignId || !hubId || !canApplyToCampaign) return;
        try {
            await applyCampaign(
                hubId,
                campaignId,
                selectedApplicant.creator?.influencer?.uuid,
                selectedApplicant.socialAccount.uuid,
                languageId,
                answers,
                socialPostId,
                quotedOffer
            );
            toast.success('Campaign applied successfully')
            clearApplicationFlow();
            dispatch(fetchApplicationsThunk({ force: true }));
        } catch (error: any) {
            console.error(error)
            const errorData = error.response?.data;
            // The offer step has no error surface of its own, so hand back to the confirmation modal.
            setShowNegotiationModal(false);
            setPendingApplication(null);
            setApplicationError(errorData);
            if (errorData?.error_code === 'insufficient_permissions') {
                clearApplicationFlow();
                return;
            }
            if (errorData?.error_code === 'application_exists') {
                toast.error('Application already exists for this campaign.')
                clearApplicationFlow();
                return;
            }
            if (errorData?.error_code === 'invalid_answer') {
                toast.error('Invalid answer for a screening question.')
                return;
            }
            toast.error("Something went wrong. Please try again.");
        }
    }

    const attachSocialPost = campaign?.attach_social_post ?? false

    const clearApplicationFlow = () => {
        setSelectedApplicant(null)
        setSelectedSocialPostId(null)
        setShowSocialPostModal(false)
        setShowNegotiationModal(false)
        setPendingApplication(null)
    }

    const specs = [
        {
            label: 'Milestone',
            value: campaignData?.milestones?.[0]?.gig_type?.name,
        },
        toValidNumber(campaign?.barter_budget) > 0 && {
            label: 'Reimbursement',
            value: campaign?.barter_reimbursement ? 'Yes' : 'No',
        },
        toValidNumber(campaign?.budget) > 0 && {
            label: 'Creator Earning',
            value: `Upto ₹${campaign?.budget}`,
            textColor: 'text-success',
        },
        toValidNumber(campaign?.barter_budget) > 0 && {
            label: campaign?.barter_reimbursement ? 'Reimbursement' : 'Barter Value',
            value: `₹${campaign?.barter_budget}`,
            textColor: 'text-success',
        },
    ].filter(Boolean) as SpecItem[]

    const requirements = [
        campaign?.requirements?.follower_count?.min && {
            label: 'Followers',
            value: `${campaign.requirements.follower_count.min}+`,
        },
        campaign?.requirements?.gender !== 'A' && {
            label: "Creator's Gender",
            value: campaign?.requirements?.gender_name,
        },
        campaign?.requirements?.age?.min && {
            label: "Creator's Age",
            value: `${campaign.requirements.age.min} - ${campaign.requirements.age.max}`,
        },
        (campaign?.requirements?.regions ?? [])?.length > 0 && {
            label: 'Creator Region(s)',
            values: campaign?.requirements?.regions,
            isList: true,
        },
        (campaign?.requirements?.cities ?? [])?.length > 0 &&
        !campaign?.requirements?.regions?.length && {
            label: 'Creator Location(s)',
            values: campaign?.requirements?.cities,
            isList: true,
        },
        campaign?.requirements?.audience_gender !== 'A' && {
            label: 'Audience Gender',
            value: campaign?.requirements?.audience_gender_name,
        },
        (campaign?.requirements?.audience_cities ?? [])?.length > 0 && {
            label: 'Audience Location(s)',
            values: campaign?.requirements?.audience_cities,
            isList: true,
        },
        (campaign?.requirements?.audience_age_groups ?? [])?.length > 0 && {
            label: 'Audience Age',
            values: campaign?.requirements?.audience_age_groups,
            isList: true,
        },
        (campaign?.requirements?.languages ?? [])?.length > 0 && {
            label: 'Languages',
            values: campaign?.requirements?.languages?.map((id) => languages[id]),
            isList: true,
        },
    ].filter(Boolean) as SpecItem[]

    const renderSpec = (item: SpecItem) => (
        <div className="campaign-detail-spec-cell">
            {item.isMultiLine && item.values ? (
                <div className="campaign-detail-spec-cell__values">
                    {item.values.map((v, i) => (
                        <span key={i} className={item.textColor ?? ''}>
                            {v}
                        </span>
                    ))}
                </div>
            ) : item.isList && item.values ? (
                <span className={`campaign-detail-spec-cell__value ${item.textColor ?? ''}`}>
                    {item.values.slice(0, 2).join(', ')}
                    {item.values.length > 2 && (
                        <button
                            type="button"
                            className="campaign-detail-show-more-btn"
                            onClick={() =>
                                setShowMore({ label: item.label, values: item.values! })
                            }
                        >
                            +{item.values.length - 2}
                        </button>
                    )}
                </span>
            ) : (
                <span className={`campaign-detail-spec-cell__value ${item.textColor ?? ''}`}>
                    {item.value}
                </span>
            )}
            <span className="campaign-detail-spec-cell__label">{item.label}</span>
        </div>
    )

    const buildSpecRows = (items: SpecItem[]) => {
        const rows: React.ReactNode[] = []
        for (let i = 0; i < items.length; i += 2) {
            rows.push(
                <React.Fragment key={i}>
                    <CampaignSpecsRow
                        col1={renderSpec(items[i])}
                        col2={items[i + 1] ? renderSpec(items[i + 1]) : <div />}
                    />
                    {i < items.length - 2 && <hr className="campaign-detail-specs-divider" />}
                </React.Fragment>
            )
        }
        return rows
    }

    const renderMilestoneMedia = (milestone: Milestone) => (
        <div className="campaign-detail-milestone-media">
            {campaign?.platform === 'yt' && (
                <div className="campaign-detail-media-tile">
                    <span className="campaign-detail-media-tile__count">
                        {milestone.content_media_types.VIDEO}
                    </span>
                    <span className="campaign-detail-media-tile__label">Video</span>
                </div>
            )}
            {campaign?.platform === 'ig' && (
                <>
                    <div className="campaign-detail-media-tile">
                        <span className="campaign-detail-media-tile__count">
                            {milestone.content_media_types.REELS}
                        </span>
                        <span className="campaign-detail-media-tile__label">Reel</span>
                    </div>
                    <div className="campaign-detail-media-tile">
                        <span className="campaign-detail-media-tile__count">
                            {milestone.content_media_types.STORY}
                        </span>
                        <span className="campaign-detail-media-tile__label">Story</span>
                    </div>
                    <div className="campaign-detail-media-tile">
                        <span className="campaign-detail-media-tile__count">
                            {milestone.content_media_types.FEED}
                        </span>
                        <span className="campaign-detail-media-tile__label">Post</span>
                    </div>
                </>
            )}
        </div>
    )

    const renderMilestoneTerms = (milestone: Milestone) => {
        const contentRights = milestone?.specifications?.content_rights_duration
        const adRights = milestone?.specifications?.ad_rights_duration
        const hasPayment = !!milestone.payment_days
        const hasContent = !!contentRights
        const hasAd = !!adRights
        if (!hasPayment && !hasContent && !hasAd) return null

        return (
            <div className="campaign-detail-milestone-media">
                {hasPayment && (
                    <div className="campaign-detail-media-tile">
                        <span className="campaign-detail-media-tile__count">
                            {milestone.payment_days}{' '}
                            <span className="campaign-detail-media-tile__subtext">
                                day{milestone.payment_days! > 1 ? 's' : ''}
                            </span>
                        </span>
                        <span className="campaign-detail-media-tile__label">Payment</span>
                    </div>
                )}
                {hasContent && (
                    <div className="campaign-detail-media-tile">
                        <span className="campaign-detail-media-tile__count">
                            {contentRights}{' '}
                            <span className="campaign-detail-media-tile__subtext">
                                month{Number(contentRights) > 1 ? 's' : ''}
                            </span>
                        </span>
                        <span className="campaign-detail-media-tile__label">Content Rights</span>
                    </div>
                )}
                {hasAd && (
                    <div className="campaign-detail-media-tile">
                        <span className="campaign-detail-media-tile__count">
                            {adRights}{' '}
                            <span className="campaign-detail-media-tile__subtext">
                                month{Number(adRights) > 1 ? 's' : ''}
                            </span>
                        </span>
                        <span className="campaign-detail-media-tile__label">Ad Rights</span>
                    </div>
                )}
            </div>
        )
    }

    const nicheTags =
        (campaign?.speciality_niche_names ?? []).length > 0
            ? campaign!.speciality_niche_names!
            : campaign?.niche_names ?? []

    const images = getCampaignImages()
    const isLoading = campaignLoading && !campaignData
    const hasBarter = toValidNumber(campaign?.barter_budget) > 0
    const showMediaSection = sortedMilestones.length > 0
    const showTermsSection = sortedMilestones.some(
        (m) =>
            m.payment_days ||
            m.specifications?.content_rights_duration ||
            m.specifications?.ad_rights_duration
    )

    if (isLoading) {
        return (
            <div className="page-content campaign-detail-page mb-3">
                <Container fluid>
                    <Skeleton height={32} width={200} className="mb-4" />
                    <Skeleton height={320} borderRadius={24} className="mb-3" />
                    <Skeleton height={220} borderRadius={20} className="mb-3" />
                    <Skeleton height={180} borderRadius={20} className="mb-3" />
                    <Skeleton height={140} borderRadius={20} />
                </Container>
            </div>
        )
    }

    if (!campaignData || !campaign) {
        return (
            <div className="page-content campaign-detail-page mb-3">
                <Container fluid>
                    <div className="campaign-detail-empty">
                        <p className="text-muted mb-0">Campaign not found.</p>
                        <Link to="/campaign" className="theme-btn btn-primary mt-3">
                            Back to campaigns
                        </Link>
                    </div>
                </Container>
            </div>
        )
    }

    return (
        <div className="page-content campaign-detail-page mb-3">
            <Container fluid>
                <header className="campaign-detail-header">
                    <div className="campaign-detail-header__left">
                        <button
                            type="button"
                            className="campaign-detail-back"
                            onClick={() => navigate('/campaign')}
                            aria-label="Back to campaigns"
                        >
                            <i className="ri-arrow-left-line" />
                        </button>
                        <div className="campaign-detail-header__meta">
                            <nav className="campaign-detail-breadcrumb" aria-label="Breadcrumb">
                                <Link to="/campaign">Campaigns</Link>
                                <span aria-hidden>/</span>
                                <span>Detail</span>
                            </nav>
                            <h1 className="campaign-detail-header__title fs-16 fw-normal">{campaign.name}</h1>
                        </div>
                    </div>
                    {campaignShareURL && (
                        <button
                            type="button"
                            className="theme-btn btn-primary-outlined campaign-detail-share"
                            onClick={handleShare}
                        >
                            <i className="ri-share-line" />
                            Share
                        </button>
                    )}
                </header>

                <div className="campaign-detail-layout">
                    <div className="campaign-detail-visual">
                        <div className="campaign-detail-hero">
                            <CampaignImageSlider images={images} />
                            {campaign.platform === 'ig' && (
                                <span className="campaign-detail-hero__platform">
                                    <img src={platformInstagram} alt="Instagram" />
                                </span>
                            )}
                            {campaign.platform === 'yt' && (
                                <span className="campaign-detail-hero__platform">
                                    <img src={platformYoutube} alt="YouTube" />
                                </span>
                            )}
                        </div>
                       
                        <section className="campaign-detail-card campaign-detail-card--intro">
                            <div className="campaign-detail-intro">
                                <h1 className="campaign-detail-intro__title">{campaign.name}</h1>
                                {brand?.category && (
                                    <p className="campaign-detail-intro__category">
                                        {brand.category}
                                    </p>
                                )}
                            </div>
                            {nicheTags.length > 0 && (
                                <div className="campaign-detail-tags">
                                    {nicheTags.map((niche, index) => (
                                        <span key={index} className="campaign-detail-tag">
                                            {niche}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </section>
                        {hubCommissionDisplay && (
                            <div className="campaign-detail-hub-commission">
                                <span className="campaign-detail-hub-commission__label">Your Earning</span>
                                <div className="campaign-detail-hub-commission__amounts">
                                    {hubCommissionDisplay.map((amount, index) => (
                                        <span key={index} className="campaign-detail-hub-commission__value">
                                            <span className="campaign-detail-hub-commission__upto">upto</span>
                                            {amount}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="campaign-detail-apply-bar">
                            {campaign.status === 'completed' ? (
                                <button disabled type="button" className="theme-btn btn-primary campaign-detail-apply">
                                    Applications Closed
                                </button>
                            ) : (canApplyToCampaign && canApply) ? (
                                <button
                                    onClick={() => setShowCreatorModal(true)}
                                    type="button"
                                    className="theme-btn btn-primary campaign-detail-apply"
                                >
                                    Apply
                                </button>
                            ) : (
                                null
                            )}
                            {
                                (!canApply && canApplyReason) && (
                                    <Alert
                                        color="danger"
                                        className="campaign-detail-apply-reason"
                                    >
                                        {canApplyReason}
                                    </Alert>
                                )
                            }
                        </div>
                    </div>

                    <div className="campaign-detail-main">


                        {specs.length > 0 && (
                            <section className="campaign-detail-card">
                                <div className="campaign-detail-specs-panel">
                                    <div className="campaign-detail-specs-grid">
                                        {buildSpecRows(specs)}
                                    </div>
                                    {hasBarter && (
                                        <p className="campaign-detail-barter-note">
                                            {campaign.barter_reimbursement
                                                ? 'You will have to buy product and it will be reimbursed by the brand.'
                                                : 'Brand will provide product for free.'}
                                        </p>
                                    )}
                                </div>
                            </section>
                        )}

                        {requirements.length > 0 && (
                            <section className="campaign-detail-card">
                                <div className="campaign-detail-specs-panel">
                                    <div className="campaign-detail-specs-grid">
                                        {buildSpecRows(requirements)}
                                    </div>
                                </div>
                            </section>
                        )}

                        {showMediaSection && (
                            <section className="campaign-detail-card">
                                <h3 className="campaign-detail-card__heading">Media Requirements</h3>
                                <div className="campaign-detail-milestone-list">
                                    {sortedMilestones.map((milestone, i) => (
                                        <div key={milestone.id ?? i} className="campaign-detail-milestone-block">
                                            {milestone.gig_type.slug !== 'general' && (
                                                <h4 className="campaign-detail-milestone-block__title">
                                                    {milestoneCount > 1
                                                        ? `#${milestone.sort_order} `
                                                        : ''}
                                                    {milestone.gig_type.name}
                                                </h4>
                                            )}
                                            {renderMilestoneMedia(milestone)}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {showTermsSection && (
                            <section className="campaign-detail-card">
                                <h3 className="campaign-detail-card__heading">Campaign Terms</h3>
                                <div className="campaign-detail-milestone-list">
                                    {sortedMilestones.map((milestone, i) => {
                                        const terms = renderMilestoneTerms(milestone)
                                        if (!terms) return null
                                        return (
                                            <div
                                                key={`terms-${milestone.id ?? i}`}
                                                className="campaign-detail-milestone-block"
                                            >
                                                {terms}
                                            </div>
                                        )
                                    })}
                                </div>
                            </section>
                        )}

                        {(brand && (brand.website_url || brand.description)) && (
                            <section className="campaign-detail-card">
                                <h3 className="campaign-detail-card__heading">About Brand</h3>
                                <h6 className="campaign-detail-brand-name">{brand.name}</h6>
                                {brand.website_url && (
                                    <a
                                        href={
                                            brand.website_url.startsWith('http')
                                                ? brand.website_url
                                                : `https://${brand.website_url}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="campaign-detail-brand-link"
                                    >
                                        {brand.website_url.split('?')[0]}
                                    </a>
                                )}
                                {brand.description && (
                                    <p className="campaign-detail-brand-desc">{brand.description}</p>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            </Container>

            <ShowMoreContentModal
                isActive={!!showMore}
                onClose={() => setShowMore(null)}
                showMore={showMore}
            />

            <SelectCreatorModal
                isActive={!!showCreatorModal}
                campaign={campaign}
                onClose={() => setShowCreatorModal(false)}
                onSelect={(creator, socialAccount) => {
                    setSelectedApplicant({ creator, socialAccount })
                    setSelectedSocialPostId(null)
                    setApplicationError(null)
                    if (attachSocialPost) {
                        setShowSocialPostModal(true)
                    }
                }}
            />

            {selectedApplicant && attachSocialPost && (
                <CampaignSocialPostSelectionModal
                    isActive={showSocialPostModal}
                    socialAccount={selectedApplicant.socialAccount}
                    influencerId={selectedApplicant.creator?.influencer?.uuid}
                    onContinue={(socialPostId) => {
                        setSelectedSocialPostId(socialPostId)
                        setShowSocialPostModal(false)
                    }}
                    onClose={clearApplicationFlow}
                />
            )}

            <CampaignApplicationConfirmationModal
                questions={campaignData?.questions ?? []}
                onApply={(languageId, answers) =>
                    handleApply(languageId, answers, selectedSocialPostId ?? undefined)
                }
                campaign={campaign}
                applicationError={applicationError}
                isActive={
                    !!selectedApplicant &&
                    !showSocialPostModal &&
                    !showNegotiationModal &&
                    (!attachSocialPost || !!selectedSocialPostId)
                }
                onClose={clearApplicationFlow}
                applicant={selectedApplicant ?? undefined}
                negotiable={campaign?.fixed_offer === 'n'}
                onRequestHigherPayout={(languageId, answers) => {
                    setPendingApplication({ languageId, answers })
                    setApplicationError(null)
                    setShowNegotiationModal(true)
                }}
            />
            <CampaignApplicationNegotiationModal
                isActive={showNegotiationModal}
                onClose={clearApplicationFlow}
                applicant={selectedApplicant ?? undefined}
                gigAmount={toValidNumber(campaign?.budget)}
                languageId={pendingApplication?.languageId}
                answers={pendingApplication?.answers}
                onApply={(languageId, answers, quotedOffer) =>
                    handleApply(languageId, answers, selectedSocialPostId ?? undefined, quotedOffer)
                }
            />
            <InsufficientPermissionsError
                isActive={!!applicationError?.missing_permissions}
                onClose={() => setApplicationError(null)}
                missing_permissions={applicationError?.missing_permissions ?? {}}
            />
        </div>
    )
}

export default CampaignDetail
